import { useMemo, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Alert,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import {
  AUTH_REQUEST_KEYS,
  authV2PasswordResetConfirm,
  authV2PasswordResetRequest,
  authV2Selector,
  toAuthV2Error,
} from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { AuthPasswordStrength } from "@/components/AuthPasswordStrength";

const AuthResetPassword = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authV2 = useSelector(authV2Selector);
  const requestLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.passwordResetRequest]);
  const confirmLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.passwordResetConfirm]);
  const token = searchParams.get("token") ?? "";
  const isConfirmMode = Boolean(token);

  const [isDone, setDone] = useState(false);

  const title = useMemo(() => (isConfirmMode ? "Set a new password" : "Reset your password"), [isConfirmMode]);

  const navigateToSignIn = () => {
    navigate("/auth/sign-in");
  };

  const requestFormik = useFormik({
    initialValues: {
      email: (location.state as { email?: string })?.email ?? "",
    },
    onSubmit: async (values) => {
      const result = await dispatch(authV2PasswordResetRequest({ email: values.email.trim() }));
      if (result.error) {
        const parsedError = toAuthV2Error(result.payload);
        dispatch(
          openSnackbar({
            message: parsedError.message,
            description: parsedError.errorDescriptions,
            intent: "danger",
          }),
        );
        return;
      }

      setDone(true);
      dispatch(
        openSnackbar({
          message: "Password reset email sent.",
          intent: "success",
        }),
      );
    },
    validationSchema: yup.object({
      email: yup.string().trim().required("Please enter your email.").email("Please enter a valid email address."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  const confirmFormik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    onSubmit: async (values) => {
      const result = await dispatch(
        authV2PasswordResetConfirm({
          token,
          newPassword: values.newPassword,
        }),
      );
      if (result.error) {
        const parsedError = toAuthV2Error(result.payload);
        dispatch(
          openSnackbar({
            message: parsedError.message,
            description: parsedError.errorDescriptions,
            intent: "danger",
          }),
        );
        return;
      }

      setDone(true);
      dispatch(
        openSnackbar({
          message: "Password updated. You can sign in now.",
          intent: "success",
        }),
      );
    },
    validationSchema: yup.object({
      newPassword: yup
        .string()
        .required("Please enter a new password.")
        .min(14, "Please use a password with at least 14 characters.")
        .matches(/\d/, "Password must contain at least 1 number.")
        .matches(/[^a-zA-Z0-9]/, "Password must contain at least 1 special character."),
      confirmPassword: yup
        .string()
        .required("Please confirm your password.")
        .oneOf([yup.ref("newPassword")], "Passwords do not match."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">{title}</Typography>
        <Typography level="body-md">
          {isConfirmMode ? "Choose a new password for your account." : "Enter your email to receive a reset link."}
        </Typography>
      </Stack>

      <AuthCard onSubmit={() => (isConfirmMode ? confirmFormik : requestFormik).handleSubmit()}>
        <Stack rowGap={6}>
          {isDone ? (
            <Alert
              intent="neutral"
              variant="filled"
              description={
                isConfirmMode ? "Your password has been changed" : "Check your email for reset instructions."
              }
            />
          ) : isConfirmMode ? (
            <>
              <FormControl error={!!confirmFormik.errors.newPassword}>
                <FormLabel size="md">New password</FormLabel>
                <Input
                  type="password"
                  value={confirmFormik.values.newPassword}
                  onChange={(event) => {
                    confirmFormik.setFieldValue("newPassword", event.target.value);
                    if (confirmFormik.errors.newPassword) {
                      confirmFormik.setFieldError("newPassword", undefined);
                    }
                  }}
                  size="lg"
                />
                {confirmFormik.errors.newPassword ? (
                  <FormHelperText>
                    <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                    {confirmFormik.errors.newPassword}
                  </FormHelperText>
                ) : null}
              </FormControl>
              <FormControl error={!!confirmFormik.errors.confirmPassword}>
                <FormLabel size="md">Confirm password</FormLabel>
                <Input
                  type="password"
                  value={confirmFormik.values.confirmPassword}
                  onChange={(event) => {
                    confirmFormik.setFieldValue("confirmPassword", event.target.value);
                    if (confirmFormik.errors.confirmPassword) {
                      confirmFormik.setFieldError("confirmPassword", undefined);
                    }
                  }}
                  size="lg"
                />
                {confirmFormik.errors.confirmPassword ? (
                  <FormHelperText>
                    <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                    {confirmFormik.errors.confirmPassword}
                  </FormHelperText>
                ) : null}
              </FormControl>
              <AuthPasswordStrength password={confirmFormik.values.newPassword} />
              <Button
                variant="primary"
                loading={confirmLoading}
                size="lg"
                endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                type="submit"
              >
                Update password
              </Button>
            </>
          ) : (
            <>
              <FormControl error={!!requestFormik.errors.email}>
                <FormLabel size="md">Email</FormLabel>
                <Input
                  placeholder="Your email address"
                  type="email"
                  value={requestFormik.values.email}
                  onChange={(event) => {
                    requestFormik.setFieldValue("email", event.target.value);
                    if (requestFormik.errors.email) {
                      requestFormik.setFieldError("email", undefined);
                    }
                  }}
                  size="lg"
                />
                {requestFormik.errors.email ? (
                  <FormHelperText>
                    <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                    {requestFormik.errors.email}
                  </FormHelperText>
                ) : null}
              </FormControl>
              <Button
                size="lg"
                variant="primary"
                loading={requestLoading}
                endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                type="submit"
              >
                Send reset link
              </Button>
            </>
          )}
        </Stack>
      </AuthCard>
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={navigateToSignIn}
      >
        Go back
      </Button>
    </>
  );
};

export { AuthResetPassword };
